import type { AILoadingInfo } from "@evidenceb/ai-handler";
import type { MinimalData } from "../interfaces/Data";
import type { PRLockStatus } from "../interfaces/PedagogicalResourcesManagement";

export type LegacyPRLockStatus = {
  moduleIds?: string[];
  objectiveIds?: string[];
  activityIds?: string[];
};

export type PRLockStatusFromAPI =
  | Partial<PRLockStatus>
  | LegacyPRLockStatus
  | undefined;

/**
 * Upgrades flat PRLockStatus to module-scoped nested structure.
 * For each locked objective/activity, finds all modules it belongs to and adds it to those module buckets.
 * Tracks which modules are explicitly locked.
 * Initializes all modules with empty objectives/activities arrays and locked = false.
 */
export const upgradeLockStatusToModuleScoped = (
  flatStatus: LegacyPRLockStatus,
  data: MinimalData,
): PRLockStatus => {
  const moduleScopedStatus: PRLockStatus = {
    modules: {},
  };

  // Initialize all modules with empty arrays and locked = false
  data.modules.forEach((mod) => {
    moduleScopedStatus.modules[mod.id] = {
      locked: false,
      objectiveIds: [],
      activityIds: [],
    };
  });

  // Mark locked modules
  (flatStatus.moduleIds ?? []).forEach((moduleId) => {
    if (moduleScopedStatus.modules[moduleId]) {
      moduleScopedStatus.modules[moduleId].locked = true;
    }
  });

  // Distribute locked objectives to their modules
  (flatStatus.objectiveIds ?? []).forEach((objectiveId) => {
    data.modules.forEach((mod) => {
      if (mod.objectiveIds.includes(objectiveId)) {
        if (
          !moduleScopedStatus.modules[mod.id].objectiveIds.includes(objectiveId)
        ) {
          moduleScopedStatus.modules[mod.id].objectiveIds.push(objectiveId);
        }
      }
    });
  });

  // Distribute locked activities to their modules (via their objectives)
  (flatStatus.activityIds ?? []).forEach((activityId) => {
    data.modules.forEach((mod) => {
      mod.objectiveIds.forEach((objId) => {
        const objective = data.objectives.find((o) => o.id === objId);
        if (objective?.activityIds.includes(activityId)) {
          if (
            !moduleScopedStatus.modules[mod.id].activityIds.includes(activityId)
          ) {
            moduleScopedStatus.modules[mod.id].activityIds.push(activityId);
          }
        }
      });
    });
  });

  return moduleScopedStatus;
};

export const isModuleScopedLockStatus = (
  lockStatus: PRLockStatusFromAPI,
): lockStatus is Partial<PRLockStatus> => {
  return (
    !!lockStatus && typeof lockStatus === "object" && "modules" in lockStatus
  );
};

export const normalizeLockStatusFromAPI = (
  lockStatus: PRLockStatusFromAPI,
  data: MinimalData,
): PRLockStatus => {
  if (isModuleScopedLockStatus(lockStatus)) {
    return completeLockStatus(lockStatus);
  }

  return upgradeLockStatusToModuleScoped(lockStatus ?? {}, data);
};

/**
 * Adds the required keys in case the locked status obtained from the backends
 * lacks them
 */
export const completeLockStatus = (
  partial: Partial<PRLockStatus> | undefined,
): PRLockStatus => {
  const modules = Object.entries(partial?.modules ?? {}).reduce<
    PRLockStatus["modules"]
  >((acc, [moduleId, moduleLockStatus]) => {
    acc[moduleId] = {
      locked: moduleLockStatus?.locked ?? false,
      objectiveIds: moduleLockStatus?.objectiveIds ?? [],
      activityIds: moduleLockStatus?.activityIds ?? [],
      ...(moduleLockStatus?.adaptiveTestOnlyUnlocked && {
        adaptiveTestOnlyUnlocked: true,
      }),
    };
    return acc;
  }, {});

  return { modules };
};

/**
 * Merge multiple lock statuses from different classrooms into a single lock status.
 * For each module, combines locked objectives and activities using deduplication.
 * The adaptive test skips locked resources as soon as one classroom asks for it.
 */
export const mergeMultipleLockStatuses = (
  lockStatuses: PRLockStatus[],
): PRLockStatus => {
  const mergedLockStatus: PRLockStatus = {
    modules: {},
  };

  lockStatuses.forEach((lockStatus) => {
    // Merge modules from this lock status
    Object.entries(lockStatus.modules).forEach(
      ([moduleId, moduleLockStatus]) => {
        if (!mergedLockStatus.modules[moduleId]) {
          mergedLockStatus.modules[moduleId] = moduleLockStatus;
        } else {
          // Merge objectives and activities, deduplicating
          mergedLockStatus.modules[moduleId] = {
            locked:
              mergedLockStatus.modules[moduleId].locked ||
              moduleLockStatus.locked,
            objectiveIds: [
              ...new Set([
                ...mergedLockStatus.modules[moduleId].objectiveIds,
                ...moduleLockStatus.objectiveIds,
              ]),
            ],
            activityIds: [
              ...new Set([
                ...mergedLockStatus.modules[moduleId].activityIds,
                ...moduleLockStatus.activityIds,
              ]),
            ],
            ...((mergedLockStatus.modules[moduleId].adaptiveTestOnlyUnlocked ||
              moduleLockStatus.adaptiveTestOnlyUnlocked) && {
              adaptiveTestOnlyUnlocked: true,
            }),
          };
        }
      },
    );
  });

  return mergedLockStatus;
};

export const isAIWithLockStatus = (ai: AILoadingInfo): boolean => {
  return !ai.error && !!ai.instance.lock;
};
